/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import {
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Button,
  Text,
  VStack,
  Box,
  useToast,
  Image,
} from '@chakra-ui/react';
import { useQueryClient } from 'react-query';
import { axiosInstance } from '@partiu/api/axios';
import { DashboardHooks } from '@partiu/api/dashboard/hooks';
import { ProductERP } from '@partiu/api/dashboard/types';
import { Product, Plan } from './components/interfaces';
import { badges } from '../ModulesSuperApp/steps/Step4';
import { images } from './components/atoms/utils';
import { useTheme } from '@partiu/ui/contexts';

export type MapIntegrationAction = 'created' | 'updated' | 'deleted';

interface MapIntegrationPayload {
  product: Product;
  productERP: ProductERP;
  planFound?: Plan | null;
}

export const mapIntegrationPlanProduct = async ({
  product,
  productERP,
  planFound,
}: MapIntegrationPayload): Promise<MapIntegrationAction> => {
  const existingProductRelation: any = productERP?.products?.find?.(
    (productRelation: any) => productRelation.product === product.id
  );

  const payload = {
    plan: planFound?.id ?? null,
    product: product.id,
  };

  if (
    existingProductRelation &&
    ((planFound && existingProductRelation.plan === planFound.id) ||
      ((!planFound || !planFound?.id) && !existingProductRelation.plan))
  ) {
    await axiosInstance.delete(`/map_integration_plan_staff/${existingProductRelation.id}/`);
    return 'deleted';
  }

  await axiosInstance.patch(`/map_integration_plan_staff/${productERP.id}/`, payload);
  return 'updated';
};

interface CreateMapintegrationRefOptions {
  targets?: ProductERP[];
  fromFilter?: boolean;
}

export interface CreateMapintegrationRef {
  open: (
    product: Product,
    productERP: ProductERP,
    planFound?: Plan | null,
    options?: CreateMapintegrationRefOptions
  ) => void;
}

interface CreateMapintegrationProps {
  refetch: () => void;
}

const CreateMapintegration = forwardRef<CreateMapintegrationRef, CreateMapintegrationProps>(({ refetch }, ref) => {
  const { colors } = useTheme();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [productERP, setProductERP] = useState<ProductERP | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [planFound, setPlanFound] = useState<Plan | null>(null);
  const [targetPlans, setTargetPlans] = useState<ProductERP[]>([]);
  const [openedFromFilter, setOpenedFromFilter] = useState(false);
  const toast = useToast();
  const cancelRef = React.useRef<HTMLButtonElement>(null);

  const handleClose = () => {
    setIsOpen(false);
    setSelectedProduct(null);
    setProductERP(null);
    setPlanFound(null);
    setTargetPlans([]);
    setOpenedFromFilter(false);
  };

  const handleConfirm = async () => {
    if (!selectedProduct) return;

    const plansToUpdate = targetPlans.length ? targetPlans : productERP ? [productERP] : [];
    if (plansToUpdate.length === 0) return;

    setIsLoading(true);
    try {
      const results = await Promise.all(
        plansToUpdate.map((plan) =>
          mapIntegrationPlanProduct({
            product: selectedProduct,
            productERP: plan,
            planFound,
          })
        )
      );
      const deletedCount = results.filter((result) => result === 'deleted').length;
      const updatedCount = results.filter((result) => result === 'updated').length;
      const createdCount = results.filter((result) => result === 'created').length;
      const totalProcessed = plansToUpdate.length;

      console.log( 'plansToUpdate', plansToUpdate);
      const namePlan = plansToUpdate[0]?.name_plan || plansToUpdate[0]?.plan_slug_integration;
      toast({
        title: totalProcessed > 1 ? 'Planos atualizados' : deletedCount ? 'Produto removido' : 'Produto vinculado',
        description:
          totalProcessed > 1
            ? `${totalProcessed} plano(s) filtrado(s) foram processados (${createdCount} criado(s), ${updatedCount} atualizado(s), ${deletedCount} removido(s)).`
            : deletedCount
              ? `${selectedProduct?.name} foi removido do plano ${namePlan}`
              : `${planFound?.name || selectedProduct?.name} foi vinculado ao ${namePlan}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      handleClose();

      refetch();
      queryClient.refetchQueries([DashboardHooks.Modules]);
      queryClient.refetchQueries([DashboardHooks.AllMapIntegrationPlans]);
      queryClient.refetchQueries([DashboardHooks.PlansIntegration]);
      queryClient.refetchQueries([DashboardHooks.ErpPlans]);

      setIsLoading(false);

    } catch (error) {
      toast({
        title: 'Erro ao vincular plano',
        description: 'Tente novamente',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      open: (product, productERPData, planFoundData, options) => {
        setIsOpen(true);
        setProductERP(productERPData);
        setSelectedProduct(product);
        setPlanFound(planFoundData || null);
        const targets = options?.targets?.length ? options.targets : [productERPData];
        setTargetPlans(targets);
        setOpenedFromFilter(Boolean(options?.fromFilter));
      },
    }),
    []
  );

  return (
    <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={handleClose} size="md">
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="md" fontWeight="semibold" textAlign="center" pb={2}>
            Vincular Plano
          </AlertDialogHeader>

          <AlertDialogBody>
            <VStack spacing={3} align="stretch">
              {openedFromFilter && (
                <Box p={3} bg="yellow.50" borderLeft="4px solid" borderColor="yellow.400" borderRadius="md">
                  <Text fontSize="sm" fontWeight="semibold" color="yellow.700">
                    {targetPlans.length} plano(s) filtrado(s) serão atualizados.
                  </Text>
                  <Text fontSize="xs" color="yellow.600">
                    Todos os itens exibidos na listagem atual receberão esta configuração.
                  </Text>
                </Box>
              )}

              <Text fontSize="sm" color="gray.600" textAlign="center" mb={2}>
                Vincular produto ao seu plano interno
              </Text>

              {/* Layout horizontal compacto */}
              <Box p={4} bg="gray.50" borderRadius="lg" border="1px solid" borderColor="gray.200">
                <VStack spacing={3}>
                  {/* Produto */}
                  <Box width="100%">
                    <Text fontSize="xs" color="gray.500" mb={1} fontWeight="medium">
                      PRODUTO
                    </Text>
                    <Box
                      display="flex"
                      alignItems="center"
                      px={3}
                      py={2}
                      gap={2}
                      borderRadius="md"
                      bg="white"
                      border="1px solid"
                      borderColor="blue.200"
                    >
                      {selectedProduct?.default_product.id === 14 ? (
                        <Image
                          src={selectedProduct?.metadata?.logo?.value as string}
                          alt={selectedProduct?.name || 'N/A'}
                          style={{ height: 50, marginRight: 8, verticalAlign: 'middle' }}
                        />
                      ) : (
                        <Box
                          position="relative"
                          bg="white"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          width="50px"
                          height="50px"
                        >
                          {selectedProduct?.default_product.id !== 14 && (
                            <Box
                              position={'absolute'}
                              mixBlendMode={'color'}
                              width="100%"
                              height={'100%'}
                              bg={colors.primaryColor}
                            />
                          )}
                          <Image
                            src={
                              (selectedProduct?.metadata?.logo?.value as string) ||
                              images[selectedProduct?.default_product.name as keyof typeof images] ||
                              ''
                            }
                            alt={selectedProduct?.default_product.name || 'N/A'}
                            style={{ height: 50 }}
                          />
                        </Box>
                      )}

                      <Text fontWeight="semibold" color="blue.700" fontSize="sm">
                        {selectedProduct?.default_product.id === 14
                          ? selectedProduct?.name
                          : selectedProduct?.name || 'N/A'}
                      </Text>
                    </Box>
                  </Box>

                  {/* Plano do Produto */}
                  {planFound && (
                    <Box width="100%">
                      <Text fontSize="xs" color="gray.500" mb={1} fontWeight="medium">
                        PLANO DO PRODUTO
                      </Text>
                      <Box px={3} py={2} borderRadius="md" bg="purple.50" border="1px solid" borderColor="purple.200">
                        <Box display="flex" alignItems="center">
                          {(() => {
                            const badge = badges.find((badge) => badge.level === planFound?.level?.rank);
                            return badge?.image ? (
                              <img
                                src={badge.image}
                                alt={badge.name || 'Badge'}
                                style={{ height: 50, marginRight: 8, verticalAlign: 'middle' }}
                              />
                            ) : null;
                          })()}
                          <Text fontWeight="semibold" color="purple.700" fontSize="sm">
                            {planFound?.name || 'N/A'}
                          </Text>
                        </Box>
                        {(() => {
                          const badge = badges.find((badge) => badge.level === planFound?.level?.rank);
                          if (!badge?.image) {
                            return (
                              <Text fontSize="xs" color="purple.600" mt={1}>
                                N/A
                              </Text>
                            );
                          }
                          return null;
                        })()}
                      </Box>
                    </Box>
                  )}
                </VStack>
              </Box>

              <Text fontSize="xs" color="gray.500" textAlign="center">
                Esta ação criará uma vinculação entre os itens selecionados
              </Text>
            </VStack>
          </AlertDialogBody>

          <AlertDialogFooter pt={3}>
            <Button ref={cancelRef} onClick={handleClose} variant="outline" size="sm" mr={3}>
              Cancelar
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleConfirm}
              isLoading={isLoading}
              isDisabled={!selectedProduct || !productERP}
              size="sm"
            >
              Confirmar
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
});

CreateMapintegration.displayName = 'CreateMapintegration';

export default CreateMapintegration;
